import React, { useEffect } from 'react';
// Since mapbox-gl v3.11.1 CSS is already globally imported in index.html, this import can be kept or removed
import 'mapbox-gl/dist/mapbox-gl.css';
import Legend from './Legend';
import LayerControl from './LayerControl';
import { 
  shouldShowAutoPath, 
  shouldShowCustomPath, 
  shouldShowDistanceLabels,
  hasPathsToShow,
  getMapContainerClassName
} from '../Services';
import { useTsp } from '../context/TspContext';
import { useMapInitialization } from './hooks/useMapInitialization';
import { useMapInteractions } from './hooks/useMapInteractions';
import { useMapData } from './hooks/useMapData';
import './styles/index.js';

/**
 * TSP Map Component
 * Uses Context API instead of props
 */
export default function MapComponent() {
  // Get all required state from context
  const {
    currentMode,
    setupSubmode,
    locations,
    setLocations,
    startId,
    setStartId,
    setPath,
    visiblePaths,
    mapPointSelectCallback,
    setMapRef
  } = useTsp();
  
  // Destructure paths object, now it's always an object
  const autoPath = visiblePaths.auto || [];
  const customPath = visiblePaths.custom || [];
  
  // Debug log to check received paths
  useEffect(() => {
    console.log('[TSP-MAP] Visible paths received:', {
      autoPathLength: Array.isArray(visiblePaths.auto) ? visiblePaths.auto.length : 0,
      hasCustom: Array.isArray(visiblePaths.custom),
      customPathLength: Array.isArray(visiblePaths.custom) ? visiblePaths.custom.length : 0
    });
  }, [visiblePaths]);
  
  // Use service functions to calculate various path display flags
  const showAutoPath = shouldShowAutoPath(currentMode);
  const showCustomPath = shouldShowCustomPath(currentMode);
  const showDistanceLabels = shouldShowDistanceLabels(currentMode);
  

  
  // Calculate if there is path data, used to decide whether to show legend
  const { hasAutoPaths, hasCustomPaths } = hasPathsToShow(showAutoPath, showCustomPath, autoPath, customPath);

  // Initialize map
  const {
    mapContainer,
    map,
    mapError,
    mapReady,
    sourcesReadyRef,
    isMountedRef
  } = useMapInitialization();

  // Setup map interactions
  useMapInteractions({
    map,
    mapReady,
    sourcesReadyRef,
    isMountedRef,
    currentMode,
    setupSubmode,
    locations,
    setLocations,
    mapPointSelectCallback
  });

  // Handle map data updates
  useMapData({
    map,
    mapReady,
    sourcesReadyRef,
    isMountedRef,
    locations,
    startId,
    autoPath,
    customPath,
    showAutoPath,
    showCustomPath,
    showDistanceLabels,
    currentMode
  });

  // Register map reference for jump-to-location functionality
  useEffect(() => {
    if (map.current && mapReady) {
      setMapRef(map.current);
    }
  }, [map, mapReady, setMapRef]);

  // Render error state if map initialization failed
  if (mapError) {
    return (
      <div className="map-error">
        <h3>Error Loading Map</h3>
        <p>{mapError}</p>
      </div>
    );
  }

  // Get map container class name based on mode
  const mapContainerClassName = getMapContainerClassName(currentMode);

  return (
    <div className={mapContainerClassName}>
      <div ref={mapContainer} className="map-container" />
      {/* Layer control panel */}
      <LayerControl 
        map={map.current} 
        mapReady={mapReady}
        currentMode={currentMode}
      />
      {(hasAutoPaths || hasCustomPaths) && <Legend />}
    </div>
  );
} 